import { useState } from "react"
import { router } from "@inertiajs/react"
import { BellRing } from "lucide-react"
import Swal from "sweetalert2"
import WatchlistModal from "./WishlistModal"

const toArray = (value) => {
    if (Array.isArray(value)) return value
    if (value == null || value === "") return []
    return [value]
}

/**
 * Merge the page's anchor identity (`context`: the brand / model / category
 * baked into the URL path) with the query-param `activeFilters` into the flat
 * structured shape the watchlist endpoint stores. Mirrors the filter sidebar's
 * `normalizeInitial` so a watchlist captures exactly what the shopper sees.
 */
const buildFilters = (activeFilters, context) => {
    const base = activeFilters ?? {}
    const ctx = context ?? {}
    const anchorSubcategories = toArray(ctx.subcategories)
    // The Category control merges parent + commercial categories (bare slugs)
    // with subcategory groups (`sg:`), seeded from `parentCategory` and/or
    // `categories` — mirror the sidebar's `normalizeInitial`.
    const anchorCategories = [
        ...(ctx.parentCategory ? [ctx.parentCategory] : []),
        ...toArray(ctx.categories),
    ]
    return {
        brands: ctx.brand ? [ctx.brand] : toArray(base.brands),
        models: ctx.model ? [ctx.model] : toArray(base.models),
        parent_categories: [
            ...new Set([...anchorCategories, ...toArray(base.parent_categories)]),
        ],
        subcategories: anchorSubcategories.length
            ? anchorSubcategories
            : toArray(base.subcategories),
        calibers: toArray(base.calibers),
        conditions: toArray(base.conditions),
    }
}

export default function WatchlistEmptyState({
    message = "No active auctions — add to your watchlist.",
    brandName,
    brandSlug,
    activeFilters,
    context,
    endpoint = "/watchlist",
}) {
    const [open, setOpen] = useState(false)

    const handleSubmit = async (data) => {
        await new Promise((resolve, reject) => {
            router.post(
                endpoint,
                {
                    ...data,
                    filters: buildFilters(activeFilters, context),
                    source_url: window.location.href,
                },
                {
                    preserveScroll: true,
                    onSuccess: () => {
                        Swal.fire({
                            toast: true,
                            position: "top-end",
                            icon: "success",
                            title: "Added to your watchlist",
                            text: "We’ll email you when matching auctions go live.",
                            showConfirmButton: false,
                            timer: 3000,
                            timerProgressBar: true,
                        })
                        resolve()
                    },
                    onError: () => reject(),
                }
            )
        })
    }

    return (
        <>
            <div className="flex flex-col items-center justify-center w-full gap-3 px-6 py-10 text-center bg-white border border-gray-300 border-dashed rounded-lg">
                <BellRing className="h-6 w-6 text-[#800020]" />
                <p className="text-base text-gray-900">{message}</p>
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="mt-2 rounded-md bg-[#800020] px-5 py-2 text-sm font-semibold text-white transition hover:bg-[#651318]"
                >
                    Add to Watchlist
                </button>
            </div>

            <WatchlistModal
                open={open}
                onOpenChange={setOpen}
                onSubmit={handleSubmit}
                brandName={brandName}
            />
        </>
    )
}
